import Loader from './Loader';
import naja from 'naja';

interface ProductData {
    selectedVariant: string | null;
    stock: number;
    isOrderable: boolean;
    currencySymbol: string;
}

export default class CartForm {
    private static productData: ProductData = {
        selectedVariant: null,
        stock: 0,
        isOrderable: true,
        currencySymbol: 'Kč',
    };

    static init(): void {
        const cartFormContainer = document.querySelector('[data-form-add-to-basket-container]') as HTMLElement;
        if (!cartFormContainer) return;

        if (cartFormContainer.dataset.currencySymbol) {
            this.productData.currencySymbol = cartFormContainer.dataset.currencySymbol;
        }

        this.initVariantSelection();
        this.initCartForm();
        this.initSnippetHandler();
        this.initFormSuccessHandler();
    }

    private static initVariantSelection(): void {
        const variantSelect = document.querySelector('select[data-variant-select]') as HTMLSelectElement;

        if (variantSelect) {
            variantSelect.addEventListener('change', (event) => {
                const target = event.target as HTMLSelectElement;
                const selectedOption = target.selectedOptions[0] as HTMLOptionElement;
                if (selectedOption) {
                    this.handleVariantChange(selectedOption);
                }
            });

            const selectedOption = variantSelect.selectedOptions[0] as HTMLOptionElement;
            if (selectedOption) {
                this.productData.selectedVariant = selectedOption.value;
                this.productData.stock = parseInt(selectedOption.dataset.variantStock || '0', 10);
                this.productData.isOrderable = selectedOption.dataset.variantOrderable === '1';
            }
        }
    }

    private static initCartForm(): void {
        const cartForm = document.querySelector('[data-form-add-to-basket]') as HTMLFormElement;
        if (!cartForm) return;

        cartForm.addEventListener('submit', (event) => {
            this.updateFormData();

            if (!this.validateForm()) {
                event.preventDefault();
                return;
            }

            Loader.show();
        });
    }

    private static initSnippetHandler(): void {
        naja.snippetHandler.addEventListener('afterUpdate', (event: { detail: { snippet: { id: string } } }) => {
            if (event.detail.snippet.id.includes('cartForm')) {
                this.initVariantSelection();
            }
        });
    }

    private static initFormSuccessHandler(): void {
        let submittedForm: HTMLFormElement | null = null;

        naja.uiHandler.addEventListener('interaction', (event: any) => {
            const element = event.detail.element as HTMLElement;
            if (element instanceof HTMLFormElement && element.classList.contains('form-add-to-basket')) {
                submittedForm = element;
            }
        });

        naja.addEventListener('success', () => {
            if (!submittedForm) return;

            const noteInput = submittedForm.querySelector('[name="note"]') as HTMLTextAreaElement | null;
            if (noteInput) {
                noteInput.value = '';
            }
            submittedForm = null;
        });
    }

    private static handleVariantChange(element: HTMLOptionElement): void {
        const variantId = element.value;
        this.productData.selectedVariant = variantId;

        this.productData.stock = parseInt(element.dataset.variantStock || '0', 10);
        this.productData.isOrderable = element.dataset.variantOrderable === '1';

        this.updateStockDisplay();
        this.updateAddToCartButton();
        this.updateFormData();

        this.loadVariantPricing(variantId);
    }

    private static async loadVariantPricing(variantId: string): Promise<void> {
        const container = document.querySelector('[data-form-add-to-basket-container]');
        if (!container) return;

        const urlTemplate = (container as HTMLElement).dataset.variantPricingUrl;
        if (!urlTemplate) return;

        const url = urlTemplate.replace('VARIANT_ID', variantId);

        try {
            Loader.show();
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.error) {
                console.error('Error loading variant pricing:', data.error);
                return;
            }

            if (data.currencySymbol) {
                this.productData.currencySymbol = data.currencySymbol;
            }

            if (data.stock !== undefined) {
                this.productData.stock = data.stock;
            }
            if (data.isOrderable !== undefined) {
                this.productData.isOrderable = data.isOrderable;
            }

            this.updateUnitPriceDisplay(data.variantPrice);
            this.updateStockDisplay();
            this.updateAddToCartButton();
        } catch (error) {
            console.error('Failed to load variant pricing:', error);
        } finally {
            Loader.hide();
        }
    }

    private static updateUnitPriceDisplay(priceVAT: number): void {
        const unitPriceElement = document.querySelector('[data-unit-price-display]') as HTMLElement;
        if (unitPriceElement) {
            unitPriceElement.textContent = `${this.formatPrice(priceVAT)} ${this.productData.currencySymbol}`;
        }

        const priceLabel = document.querySelector('[data-price-label]') as HTMLElement;
        if (priceLabel) {
            priceLabel.textContent = 'Cena';
        }
    }

    private static updateFormData(): void {
        const cartForm = document.querySelector('[data-form-add-to-basket]') as HTMLFormElement;
        if (!cartForm) return;

        let variantInput = cartForm.querySelector('[name="variant"]') as HTMLInputElement;
        if (!variantInput) {
            variantInput = document.createElement('input');
            variantInput.type = 'hidden';
            variantInput.name = 'variant';
            cartForm.appendChild(variantInput);
        }

        if (this.productData.selectedVariant) {
            variantInput.value = this.productData.selectedVariant;
        }

        const quantityInput = cartForm.querySelector('[name="quantity"]') as HTMLInputElement;
        if (quantityInput) {
            quantityInput.value = '1';
        }
    }

    private static updateStockDisplay(): void {
        const stockDisplay = document.querySelector('[data-stock-display]') as HTMLElement;
        if (!stockDisplay) return;

        const inStockText = stockDisplay.dataset.inStockText || 'Skladem';
        const outOfStockText = stockDisplay.dataset.outOfStockText || 'Není skladem';
        const pcsText = stockDisplay.dataset.pcsText || 'ks';

        let html: string;
        if (this.productData.stock > 50) {
            html = `<span class="c-33">${inStockText} > 50 ${pcsText}</span>`;
        } else if (this.productData.stock > 0) {
            html = `<span class="c-33">${inStockText} ${this.productData.stock} ${pcsText}</span>`;
        } else {
            html = `<span class="c-5">${outOfStockText}</span>`;
        }

        stockDisplay.innerHTML = html;
    }

    private static updateAddToCartButton(): void {
        const addToCartBtn = document.querySelector('[data-add-to-cart-btn]') as HTMLButtonElement;
        if (!addToCartBtn) return;

        addToCartBtn.style.display = this.productData.isOrderable ? '' : 'none';

        const noteWrapper = document.querySelector('[data-note-field-wrapper]') as HTMLElement | null;
        if (noteWrapper) {
            noteWrapper.style.display = this.productData.isOrderable ? '' : 'none';
        }
    }

    private static validateForm(): boolean {
        const variantSelect = document.querySelector('select[data-variant-select]') as HTMLSelectElement;
        if (variantSelect && !this.productData.selectedVariant) {
            alert('Prosím vyberte variantu produktu.');
            return false;
        }

        return true;
    }

    private static formatPrice(price: number, decimals: number = 2): string {
        return price.toLocaleString('cs-CZ', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        });
    }
}
